import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { RotatingLines } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';

import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import CommonButtonLink from '../../../../components/common-button-link/CommonButtonLink';
import Icon from '../../../../icons/Icon';
import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import SectionTitle from '../../components/section-title/SectionTitle';
import PlatformDropdowns from './components/PlatformDropdowns';
import { MEDIA_CONTENT_PLACEMENT, STORY_PLATFORM_TYPES } from '../../../../constants';
import { isStoryMediaUploading } from '../../media/storyMediaItem';
import { primarySelectedMedia } from '../../selectors/selectedMediaSelectors';
import { STORY_FORM_FIELDS } from '../../constants';

import scss from './ParamsSection.module.scss';

function ParamsSection() {
  const { t } = useTranslation('story');

  const { submitting, change } = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  const sessionId = useSelector((state) => state.story.sessionId);
  const isPlatformsLoading = useSelector((state) => state.story.isPlatformsLoading);
  const selectedMedia = useSelector((state) => state.story.selectedMedia);
  const prevSessionIdRef = useRef(sessionId);
  const primaryMedia = useSelector(primarySelectedMedia);
  const placement = selectedMedia.length > 1 ? MEDIA_CONTENT_PLACEMENT.MULTIPLE : MEDIA_CONTENT_PLACEMENT.SINGLE;
  const prevPlacementRef = useRef(placement);

  const hasUploadingMedia = useMemo(
    () => (values?.[STORY_FORM_FIELDS.MEDIA] ?? []).some(isStoryMediaUploading),
    [values],
  );

  const hasSelectedSettings = useMemo(
    () => Object.values(STORY_PLATFORM_TYPES).some((platform) => values?.[platform]?.length > 0),
    [values],
  );

  const clearPlatformSelections = useCallback(() => {
    Object.values(STORY_PLATFORM_TYPES).forEach((platform) => {
      change(platform, []);
    });
  }, [change]);

  useEffect(() => {
    const prevSessionId = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;

    if (prevSessionId && prevSessionId !== sessionId) {
      clearPlatformSelections();
    }
  }, [sessionId, clearPlatformSelections]);

  useEffect(() => {
    const prevPlacement = prevPlacementRef.current;
    prevPlacementRef.current = placement;

    if (prevPlacement && prevPlacement !== placement) {
      clearPlatformSelections();
    }
  }, [placement, clearPlatformSelections]);

  return (
    <div className={scss.wrapper}>
      {isPlatformsLoading ? (
        <div className={scss.loader}>
          <RotatingLines strokeColor='#fdbf47' strokeWidth='3' animationDuration='0.75' width={24} visible />
        </div>
      ) : null}

      <div className={scss.titleBlock}>
        <SectionTitle title={t('settings')} subtitle={t('chooseFormatsToAdjustYourContentFor')}>
          <CommonButtonLink
            onClick={clearPlatformSelections}
            styleColor={BUTTON_STYLES.PRIMARY}
            LeftIcon={ClearOutlinedIcon}
            type='button'
            disabled={isPlatformsLoading || !hasSelectedSettings}>
            {t('clearSettings')}
          </CommonButtonLink>
        </SectionTitle>
      </div>

      <PlatformDropdowns className={scss.dropdowns} mediaType={primaryMedia?.type} placement={placement} />

      <div className={scss.actions}>
        <TooltipButton
          type='submit'
          styleColor={BUTTON_STYLES.PRIMARY}
          maxWidth={164}
          disabled={isPlatformsLoading || hasUploadingMedia}
          isLoading={submitting}>
          <Icon icon={BuildOutlinedIcon} />
          ACTIV8
        </TooltipButton>

        <div className={scss.rightActions}></div>
      </div>
    </div>
  );
}

export default ParamsSection;
